import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DirectoryFloor } from '../utils/floors';

type Props = {
  floors: DirectoryFloor[];
  /** Breathing room (px) above/below the panel used for fit calc */
  marginY?: number;
  /** Overlay width as % of viewport */
  widthPct?: number;
  /** Optional lower bound to avoid over-shrinking on tiny screens */
  minScale?: number; // e.g. 0.5
};

export const FloorDirectoryOverlay: React.FC<Props> = ({
  floors,
  marginY = 24,
  widthPct = 28,
  minScale = 0.5,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  const [scale, setScale] = useState(1);
  const [naturalH, setNaturalH] = useState(0);

  // available viewport height (minus visual top/bottom margin)
  const availableH = useMemo(() => {
    const vh =
      typeof window !== 'undefined'
        ? window.visualViewport?.height ?? window.innerHeight
        : 0;
    return Math.max(0, vh - marginY * 2);
  }, [marginY]);

  useLayoutEffect(() => {
    // inline recalc logic so eslint doesn't see an external dependency
    const recalcThrottled = () => {
      // throttle via rAF so ResizeObserver + layout updates don't fight
      if (frameRef.current != null) return;

      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        const el = contentRef.current;
        if (!el) return;

        // natural height without transforms
        const h = el.scrollHeight || 0;

        // target scale, clamped
        const desiredScale =
          h > 0 ? Math.min(1, Math.max(minScale, availableH / h)) : 1;

        const EPS = 0.001;

        // only update state if it actually changed (prevents flicker loops)
        setNaturalH((prev) => (Math.abs(prev - h) > 0.5 ? h : prev));
        setScale((prev) =>
          Math.abs(prev - desiredScale) > EPS ? desiredScale : prev
        );
      });
    };

    // run immediately
    recalcThrottled();

    // watch size changes in the measured content
    if (roRef.current) {
      roRef.current.disconnect();
    }
    roRef.current = new ResizeObserver(() => recalcThrottled());
    if (contentRef.current) {
      roRef.current.observe(contentRef.current);
    }

    // watch viewport resize
    const onResize = () => recalcThrottled();
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);

    return () => {
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
      }
      if (roRef.current) {
        roRef.current.disconnect();
      }
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, [floors, availableH, minScale]);

  const panelWidth = `${widthPct}%`;
  const innerWidthPct = `${(100 / scale).toFixed(6)}%`;
  const visualH = Math.ceil(naturalH * scale); // for outer <aside> height
  const getInitials = (name: string) =>
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('');

  return (
    <aside
      aria-label="Floor directory"
      style={{
        position: 'fixed',
        left: '5%',
        top: '50%',
        transform: 'translateY(-50%)',
        width: panelWidth,
        height: visualH ? `${visualH}px` : 'auto',
        background: 'rgba(248,244,236,0.96)',
        border: '1px solid rgba(255,255,255,0.4)',
        borderLeft: '7px solid #f2a900',
        borderRadius: 4,
        overflow: 'hidden', // never show inner scrollbar
        boxShadow: '0 32px 70px rgba(0,0,0,0.34)',
        backdropFilter: 'blur(14px)',
        zIndex: 1000,
        willChange: 'transform, height',
      }}
    >
      {/* Scaled layer: width inversely scaled so visual width stays consistent */}
      <div
        style={{
          width: innerWidthPct,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {/* Untransformed content, measured by scrollHeight */}
        <div ref={contentRef} style={{ padding: '24px 28px' }}>
          {floors.map((floor, fi) => (
            <section key={fi} style={{ marginBottom: 22 }}>
              <h2
                style={{
                  background: '#092f35',
                  color: '#fff',
                  borderRadius: 2,
                  padding: '9px 12px',
                  fontWeight: 800,
                  fontSize: 18,
                  margin: 0,
                  marginBottom: 8,
                  lineHeight: 1.2,
                  letterSpacing: 0,
                }}
              >
                {floor.name}
              </h2>

              {floor.companies.map((c, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '9px 4px',
                    borderBottom:
                      i === floor.companies.length - 1
                        ? 'none'
                        : '1px solid rgba(9,47,53,0.13)',
                  }}
                >
                  {c.logo ? (
                    <img
                      src={c.logo}
                      alt={c.name}
                      style={{
                        height: 30,
                        width: 44,
                        objectFit: 'contain',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <span
                      aria-hidden="true"
                      style={{
                        width: 44,
                        height: 30,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        background: '#092f35',
                        color: '#f8f4ec',
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {getInitials(c.name)}
                    </span>
                  )}
                  <span
                    style={{
                      color: '#102326',
                      fontWeight: 800,
                      fontSize: 16,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      letterSpacing: 0,
                    }}
                  >
                    {c.name}
                  </span>
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </aside>
  );
};
